import { useState, useRef } from "react";
import { Divider, Stack, Tooltip, Button, ButtonGroup, Popover, Menu, MenuItem, Typography, TextField, Box, CircularProgress } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';


function ActionButtons({ 
  nameHidden, 
  selectedTrackRecordName, 
  trackRecordName, 
  setTrackRecordName, 
  toggleFields, 
  TRNameReset,
  error,
  helperText,
  changesPending,
  trackRecordLoading,
  isOwner,
  actions,
}) {
  const { addOnClick, saveOnClick, cloneOnClick, cloneTrackRecord, deleteOnClick, clearTRQuery } = actions;
  
  const popoverOrigins = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center',
    }
  };

  const [clonePopupAnchor, setClonePopupAnchor] = useState(null);
  const [deletePopupAnchor, setDeletePopupAnchor] = useState(null);
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const saveDropdownAnchorRef = useRef(null);

  function popoverContent(text, action, onConfirm) {
    return (
      <>
        <Typography sx={{ p: 1, textAlign: "center" }}>
          {text}
        </Typography>
        { action === "clone"
          ? (<TextField
            fullWidth
            sx={{ padding: "0 5px" }}
            size="small"
            value={trackRecordName}
            error={error}
            helperText={helperText}
            onChange={(e) => setTrackRecordName(e.target.value)}
          />) : null 
        }
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', p: 1 }}>
          <Button 
            variant="contained" 
            onClick={() => { 
              onConfirm(); 
            }}
            data-testid={`${action}-confirm-button`}
            onMouseLeave={action === "clone" ? TRNameReset : null}
          >
            Confirm
          </Button>
          <Button 
            onClick={ 
              action === "clone" 
              ? () => { 
                setClonePopupAnchor(null); 
                setTrackRecordName(selectedTrackRecordName.trackRecordName);
              } 
              : () => setDeletePopupAnchor(null)
            }
            data-testid={`${action}-cancel-button`}
          >
            Cancel
          </Button>
        </Box>
      </>
    );
  }

  const cloneConfirmOnClick = () => {
    cloneTrackRecord();
    setClonePopupAnchor(false);
  }

  const deleteConfirmOnClick = () => {
    deleteOnClick();
    setDeletePopupAnchor(false);
  }

  return(
    <Stack
      className = "track-record-action-buttons"
      direction="row"
      spacing={1}
      sx={{
        width: '100%',
        justifyContent: 'right'
      }}
    >
      {trackRecordLoading ? (
        <CircularProgress />
      ) : (
      <>
      <Tooltip title="Discard Changes">
        <span>
          <Button
            data-testid="clear-button"
            variant="outlined"
            disabled={!changesPending()} 
            size="small"
            onClick={() => clearTRQuery()}
          >
            Discard Changes
          </Button>
        </span>
      </Tooltip>

      <Tooltip
        title={
          !selectedTrackRecordName
            ? "Save New Track Record"
            : isOwner
            ? "Save Changes to Track Record"
            : "Disabled: Only the owner can save changes"
        }
      >
        <span>
          <ButtonGroup variant="contained" size="small" sx={{ "& .MuiButtonGroup-grouped": { borderColor: "rgba(255,255,255,0.3)" } }}>
            <Button
              data-testid="save-button"
              disabled={!changesPending() || (selectedTrackRecordName && !isOwner)}
              onClick={saveOnClick}
              sx={{ textTransform: "none" }}
            >
              Save Current Settings As
            </Button>
            <Button
              data-testid="save-dropdown-button"
              ref={saveDropdownAnchorRef}
              onClick={(event) => {
                saveDropdownAnchorRef.current = event.currentTarget;
                setSaveMenuAnchor(event.currentTarget);
              }}
              sx={{ minWidth: 36, px: 0.5 }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        </span>
      </Tooltip>

      <Menu
        anchorEl={saveMenuAnchor}
        open={Boolean(saveMenuAnchor)}
        onClose={() => setSaveMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          data-testid="add-button"
          onClick={() => {
            setSaveMenuAnchor(null);
            toggleFields(true);
            addOnClick();
          }}
        >
          New Track Record
        </MenuItem>
        <MenuItem
          data-testid="clone-button"
          disabled={!selectedTrackRecordName}
          onClick={() => {
            setSaveMenuAnchor(null);
            if (saveDropdownAnchorRef.current) {
              setClonePopupAnchor(saveDropdownAnchorRef.current);
              cloneOnClick();
            }
          }}
        >
          Track Record Clone
        </MenuItem>
        <MenuItem
          data-testid="delete-button"
          disabled={!isOwner || !selectedTrackRecordName}
          onClick={() => {
            setSaveMenuAnchor(null);
            if (saveDropdownAnchorRef.current && selectedTrackRecordName) {
              toggleFields(false);
              setTrackRecordName(selectedTrackRecordName.trackRecordName);
              setDeletePopupAnchor(saveDropdownAnchorRef.current);
            }
          }}
          sx={{ color: "error.main" }}
        >
          Remove track record
        </MenuItem>
      </Menu>

      {selectedTrackRecordName && (
        <Popover 
          open={Boolean(clonePopupAnchor)}
          anchorEl={clonePopupAnchor}
          onClose={() => {
            setClonePopupAnchor(null);
            setTrackRecordName(selectedTrackRecordName.trackRecordName);
          }} 
          {...popoverOrigins}
          sx={{ '& .MuiPopover-paper': { minWidth: '250px' } }}
        >
          {popoverContent("Save Track Record with name:", "clone", cloneConfirmOnClick)}
        </Popover> 
      )}

      {selectedTrackRecordName && (
        <Popover 
          open={Boolean(deletePopupAnchor)}
          anchorEl={deletePopupAnchor}
          onClose={() => setDeletePopupAnchor(null)} 
          {...popoverOrigins}
          sx={{ '& .MuiPopover-paper': { minWidth: '200px' } }}
        >
          {popoverContent(`Delete ${selectedTrackRecordName.trackRecordName}?`, "delete", deleteConfirmOnClick)}
        </Popover> 
      )}
    </>
    )}
    </Stack>
  )
}

export default ActionButtons;